// Shared transactions header — title, status-manager toggle, list/board view
// switch, and new-transaction actions. Rendered above the board
// (Kanban) view. The list view (TransactionListV2) renders its own header.

import React from 'react';

const GearIcon = ({ className = 'h-4 w-4' }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

export const TransactionsHeader = ({
    activeView = 'list',
    onSelectListView = () => {},
    onSelectBoardView = () => {},
    onCreateTransaction = () => {},
    onToggleStatusManager = () => {},
    canManageStatuses = true,
    isStatusManagerOpen = false,
    showCappingMathButton = false,
    onOpenCappingMath = () => {}
}) => {
    const isListView = activeView === 'list';
    return (
        <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-[color:var(--text-primary)]">Transactions</h1>
                {canManageStatuses && (
                    <button
                        type="button"
                        onClick={onToggleStatusManager}
                        title={isStatusManagerOpen ? 'Hide status manager' : 'Manage statuses'}
                        aria-label={isStatusManagerOpen ? 'Hide status manager' : 'Manage statuses'}
                        aria-pressed={isStatusManagerOpen}
                        className={`inline-flex h-9 w-9 items-center justify-center rounded-md border transition-colors ${
                            isStatusManagerOpen
                                ? 'border-[color:var(--color-accent)]/40 bg-[color:var(--surface-info)] text-[color:var(--color-accent)]'
                                : 'border-[color:var(--border-color)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-subtle)] hover:text-[color:var(--text-primary)]'
                        }`}
                    >
                        <GearIcon className={`h-[18px] w-[18px] transition-transform duration-200 ${isStatusManagerOpen ? 'rotate-45' : ''}`} />
                    </button>
                )}
                {showCappingMathButton && (
                    <button
                        type="button"
                        onClick={onOpenCappingMath}
                        className="inline-flex items-center gap-2 rounded-md border border-[color:var(--color-warning)]/30 bg-[color:var(--surface-warning)] px-3 py-1.5 text-sm font-semibold text-[color:var(--color-warning)] hover:bg-[color:var(--surface-warning)] hover:opacity-90"
                        title="Show capping math breakdown"
                    >
                        <span>🧮 Capping Math</span>
                    </button>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex overflow-hidden rounded-md border border-[color:var(--border-color)]">
                    <button
                        type="button"
                        onClick={onSelectListView}
                        className={`px-4 py-2 text-sm font-semibold transition-colors ${
                            isListView ? 'bg-[color:var(--color-accent)] text-white' : 'bg-[color:var(--surface-primary)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-subtle)]'
                        }`}
                        aria-pressed={isListView}
                    >
                        List
                    </button>
                    <button
                        type="button"
                        onClick={onSelectBoardView}
                        className={`px-4 py-2 text-sm font-semibold transition-colors ${
                            activeView === 'kanban' ? 'bg-[color:var(--color-accent)] text-white' : 'bg-[color:var(--surface-primary)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-subtle)]'
                        }`}
                        aria-pressed={activeView === 'kanban'}
                    >
                        Board
                    </button>
                </div>
                <button
                    type="button"
                    onClick={onCreateTransaction}
                    className="inline-flex items-center rounded-md bg-[color:var(--color-accent)] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[color:var(--color-accent)]"
                >
                    New Transaction
                </button>
            </div>
        </div>
    );
};

export default TransactionsHeader;
